import type { SwitchboardResponse } from "@t3tools/contracts";
import { Clock3Icon } from "lucide-react";
import { DefinitionPopover, KpiCard, KpiGrid } from "./dashboardKit";

export function formatSavedTime(seconds: number) {
  if (seconds < 60) return `${Math.floor(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function SwitchboardImpact({ data }: { data: SwitchboardResponse }) {
  const impact = data.impact;
  return (
    <section aria-label="Switchboard impact" className="space-y-4">
      <article className="rounded-xl border border-emerald-600/25 bg-emerald-500/5 p-5 md:p-7">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Clock3Icon className="size-4 text-emerald-600" aria-hidden />
              <h2>CN time saved</h2>
              <span className="rounded-full border border-emerald-600/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Estimated
              </span>
              <DefinitionPopover
                label="CN time saved"
                definition="Estimated dialing and screening time a CN would otherwise spend on the same attempts. Measured from provider dialing until the earliest call end, navigator handoff, or staff join. Navigator-led calls, preparation-only failures, open calls, and missing timestamps receive no time credit. This estimates avoided phone time, rather than staffing or financial savings."
              />
            </div>
            <p className="mt-3 text-5xl font-semibold tracking-tight text-foreground md:text-6xl">
              {impact.timeSavedEligibleCalls > 0 && impact.timeSavedMeasuredCalls === 0
                ? "—"
                : formatSavedTime(impact.timeSavedSeconds)}
            </p>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Ringing, voicemail, and screening handled before a CN joins — including people who
              can’t talk right now.
            </p>
          </div>
          <div className="max-w-xs rounded-lg border border-border/60 bg-background/60 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
            <p className="font-medium text-foreground">Based on measured call time</p>
            <p className="mt-1">
              {impact.timeSavedMeasuredCalls.toLocaleString()} of{" "}
              {impact.timeSavedEligibleCalls.toLocaleString()} automated dial attempts have complete
              timing. Time after a CN joins is excluded.
            </p>
          </div>
        </div>
      </article>
      <KpiGrid className="md:grid-cols-2">
        <KpiCard
          label="Care calls connected"
          value={impact.connectedCareCalls.toLocaleString()}
          tooltip="Switchboard calls with a confirmed navigator handoff or a documented connected interaction with a patient or caregiver. A provider pickup or a voicemail system alone does not count."
          footnote="Confirmed CN handoffs or documented family conversations"
        />
        <KpiCard
          label="Care calls completed"
          value={impact.completedCareCalls.toLocaleString()}
          tooltip="Distinct completed clinical encounters linked to a connected, documented patient or caregiver call. Ending an attempt or creating an automatic Switchboard contact does not establish clinical completion."
          footnote="Completed clinical encounters with connected call documentation"
        />
      </KpiGrid>
      <KpiGrid className="md:grid-cols-2">
        <KpiCard
          label="CNs using Switchboard"
          value={impact.activeNavigators.toLocaleString()}
          tooltip="Distinct navigators who made at least one actual provider dial through Daily Switchboard in the selected period and scope. Preparation-only failures do not count."
          footnote="At least one Switchboard dial in this period"
        />
        <KpiCard
          label="Calls using Switchboard"
          value={impact.switchboardShare === null ? "—" : `${impact.switchboardShare.toFixed(1)}%`}
          tooltip="Daily Switchboard provider dial attempts divided by identified outbound CN attempts in Carespaces and Dialpad for the same period, clinic, and navigator. Dialpad legs are grouped into conversations and must resolve to a CN and family. Inbound calls and preparation-only failures are excluded."
          footnote={`${impact.switchboardCalls.toLocaleString()} of ${impact.totalOutboundCalls.toLocaleString()} identified outbound CN attempts`}
        />
      </KpiGrid>
      <p className="text-xs leading-relaxed text-muted-foreground">
        {data.period.dateStart} – {data.period.dateEnd} · Headline metrics use the selected dates,
        clinic, and navigator for CNs in the current team directory. Call-detail filters apply
        below. Dialpad coverage includes calls with a resolved CN and family association.
      </p>
    </section>
  );
}
